import React, { useEffect, useState } from 'react'
import { t, Trans } from '@lingui/macro'
import classNames from 'classnames'
import ResettableInput from '../form/input/ResettableInput'
import Field from '../form/field/Field'
import Loader from '../loader/Loader'
import UserAvatar from '../user/user-avatar/UserAvatar'
import GroupAvatar from '../group/group-avatar/GroupAvatar'
import './MembersSelector.less'
import IconPeople from '../icon/IconPeople'
import {
  ButtonPrimary,
  ButtonDanger,
  ButtonLight,
  Box
} from 'front-end/src/components'
import { useFeatureFlags } from 'front-end/src/app/hooks/useFeatureFlags'
import { useDebouncedCallback } from 'use-debounce'

export interface IMembersSelectorUser {
  id: number
  display_name: string
  userIdentity: string
  image_url: string | null
  cantBeSelectedReason?: string
  added_at?: Date
}

export interface IMembersSelectorGroup {
  id: number
  name: string
  cantBeSelectedReason?: string
  added_at?: Date
}

enum MemberType {
  USER = 'USER',
  GROUP = 'GROUP'
}

function isUser(arg: any): arg is IMembersSelectorUser {
  return arg.type === MemberType.USER
}

export interface IMembersSelectorMembers {
  users: IMembersSelectorUser[]
  groups: IMembersSelectorGroup[]
}

type MembersSelectorState = {
  autocompleteUsers: IMembersSelectorUser[]
  autocompleteGroups: IMembersSelectorGroup[]
  showAutocomplete: boolean
  isAddressBookActive: boolean
  query: string
}

type MembersSelectorProps = {
  isSearching: boolean
  addressBook?: IMembersSelectorMembers
  membersSelected: IMembersSelectorMembers
  membersSearched: IMembersSelectorMembers
  onAdd: (
    user: IMembersSelectorUser | null,
    group?: IMembersSelectorGroup
  ) => void
  onRemove: (
    user: IMembersSelectorUser | null,
    group?: IMembersSelectorGroup
  ) => void
  onQueryChange: (query: string) => void
  inputRef?: (input: HTMLInputElement) => void
}

const SEARCH_DEBOUNCE = 500
const SEARCH_MINIMAL_LENGTH = 3

const MembersSelector = (props: MembersSelectorProps) => {
  const { featureFlags } = useFeatureFlags()

  const [state, setState] = useState<MembersSelectorState>({
    autocompleteUsers: [],
    autocompleteGroups: [],
    showAutocomplete: false,
    isAddressBookActive: false,
    query: ''
  })

  const setStateWrapped = (state: Partial<MembersSelectorState>) => {
    setState((prevState) => {
      return { ...prevState, ...state }
    })
  }

  useEffect(() => {
    if (!state.isAddressBookActive) {
      const { unselectedUsers, unselectedGroups } = getUnselectedUsersGroups(
        props.membersSearched?.users,
        props.membersSearched?.groups
      )

      setStateWrapped({
        autocompleteUsers: unselectedUsers,
        autocompleteGroups: unselectedGroups
      })
    }
  }, [props.membersSearched])

  const changeQuery = useDebouncedCallback((query) => {
    if (query.length < SEARCH_MINIMAL_LENGTH) {
      return
    }
    if (typeof props.onQueryChange === 'function') {
      props.onQueryChange(query)
    }
  }, SEARCH_DEBOUNCE)

  const renderUser = (user: IMembersSelectorUser, action: 'add' | 'remove') => (
    <>
      <UserAvatar user={user} size="small" />
      <span className="MembersSelector__user">
        {user.display_name}
        <span className="identity">{user.userIdentity}</span>
      </span>
      {user.cantBeSelectedReason && (
        <ButtonPrimary size="xs" className="CantBeAddedLabel">
          {user.cantBeSelectedReason}
        </ButtonPrimary>
      )}
      {!user.cantBeSelectedReason && action === 'add' && (
        <ButtonPrimary size="xs" className="Action">
          <Trans>Add</Trans>
        </ButtonPrimary>
      )}
      {action === 'remove' && (
        <ButtonPrimary size="xs" className="Action">
          <Trans>Remove</Trans>
        </ButtonPrimary>
      )}
    </>
  )

  const renderGroup = (
    group: IMembersSelectorGroup,
    action: 'add' | 'remove'
  ) => (
    <>
      <GroupAvatar
        group={{
          name: group.name
        }}
        size="small"
      />
      <span className="MembersSelector__group">
        <span className="MembersSelector__groupName">{group.name}</span> (
        <Trans>Group</Trans>)
      </span>
      {group.cantBeSelectedReason && (
        <ButtonPrimary size="xs" className="CantBeAddedLabel">
          {group.cantBeSelectedReason}
        </ButtonPrimary>
      )}
      {!group.cantBeSelectedReason && action === 'add' && (
        <ButtonPrimary size="xs" className="Action">
          <Trans>Add</Trans>
        </ButtonPrimary>
      )}
      {action === 'remove' && (
        <ButtonDanger size="xs" className="Action">
          <Trans>Remove</Trans>
        </ButtonDanger>
      )}
    </>
  )

  const addGroup = (group: IMembersSelectorGroup) => {
    if (typeof props.onAdd === 'function') {
      props.onAdd(null, {
        id: group.id,
        name: group.name,
        added_at: new Date()
      })

      setStateWrapped({
        isAddressBookActive: false,
        showAutocomplete: false,
        query: ''
      })
      changeQuery('')
    }
  }

  const removeGroup = (group: IMembersSelectorGroup) => {
    if (typeof props.onRemove === 'function') {
      props.onRemove(null, {
        id: group.id,
        name: group.name
      })
    }
  }

  const addUser = (user: IMembersSelectorUser) => {
    if (typeof props.onAdd === 'function') {
      props.onAdd({
        id: user.id,
        display_name: user.display_name,
        userIdentity: user.userIdentity,
        image_url: user.image_url,
        added_at: new Date()
      })

      setStateWrapped({
        isAddressBookActive: false,
        showAutocomplete: false,
        query: ''
      })
      changeQuery('')
    }
  }

  const removeUser = (user: IMembersSelectorUser) => {
    if (typeof props.onRemove === 'function') {
      props.onRemove({
        id: user.id,
        display_name: user.display_name,
        userIdentity: user.userIdentity,
        image_url: user.image_url
      })
    }
  }

  const renderAutocomplete = () => {
    const hasDataToShow =
      state.autocompleteUsers.length > 0 || state.autocompleteGroups.length > 0

    return (
      <>
        <Box
          position="fixed"
          top={0}
          left={0}
          zIndex={1}
          boxSize="100%"
          onClick={() =>
            setStateWrapped({
              showAutocomplete: false,
              isAddressBookActive: false
            })
          }
        />
        <Box
          position="relative"
          zIndex={2}
          className={`MembersSelector__dropdown ${
            state.isAddressBookActive ? 'right' : ''
          }`}
        >
          {props.isSearching ? (
            <Loader />
          ) : (
            <div className="MembersSelector__dropdown-content">
              {(hasDataToShow && state.query.length >= SEARCH_MINIMAL_LENGTH) ||
              (hasDataToShow && state.isAddressBookActive) ? (
                <div className="MembersSelector__members">
                  {state.autocompleteGroups.map((group) => {
                    const canBeAdded = !group.cantBeSelectedReason
                    const onClick = canBeAdded
                      ? () => addGroup(group)
                      : undefined
                    const className = classNames('MembersSelector__member', {
                      'no-click': !canBeAdded
                    })
                    return (
                      <div
                        className={className}
                        key={group.id}
                        onClick={onClick}
                      >
                        {renderGroup(group, 'add')}
                      </div>
                    )
                  })}
                  {state.autocompleteUsers.map((user) => {
                    const canBeAdded = !user.cantBeSelectedReason
                    const onClick = canBeAdded ? () => addUser(user) : undefined
                    const className = classNames('MembersSelector__member', {
                      'no-click': !canBeAdded
                    })
                    return (
                      <div
                        className={className}
                        key={user.id}
                        onClick={onClick}
                      >
                        {renderUser(user, 'add')}
                      </div>
                    )
                  })}
                </div>
              ) : (
                <span className="MembersSelector__empty">
                  {state.query.length > 0 && state.query.length < 3 ? (
                    <Trans>Minimal 3 characters</Trans>
                  ) : state.isAddressBookActive ? (
                    <Trans>No more groups in your address book</Trans>
                  ) : (
                    <Trans>No matching users or groups found</Trans>
                  )}
                </span>
              )}
            </div>
          )}
        </Box>
      </>
    )
  }

  const renderSelected = () => {
    const users = props.membersSelected.users.map((user) => ({
      ...user,
      type: MemberType.USER
    }))
    const groups = props.membersSelected.groups.map((group) => ({
      ...group,
      type: MemberType.GROUP
    }))

    const entities = (
      users as Array<IMembersSelectorGroup | IMembersSelectorUser>
    ).concat(groups)

    entities.sort((a, b) =>
      a.added_at && b.added_at ? b.added_at.getTime() - a.added_at.getTime() : 0
    )

    return (
      <div className="MembersSelector__selected">
        <div className="MembersSelector__content">
          {entities.length > 0 && (
            <div className="MembersSelector__members">
              {entities.map((member) => {
                return isUser(member) ? (
                  <div
                    className="MembersSelector__member"
                    key={member.id}
                    onClick={() => {
                      removeUser(member)
                    }}
                  >
                    {renderUser(member, 'remove')}
                  </div>
                ) : (
                  <div
                    className="MembersSelector__member"
                    key={member.id}
                    onClick={() => {
                      removeGroup(member)
                    }}
                  >
                    {renderGroup(member, 'remove')}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    )
  }

  const getUnselectedUsersGroups = (
    users: IMembersSelectorUser[],
    groups: IMembersSelectorGroup[]
  ) => {
    const selectedUserIds = props.membersSelected.users.map(({ id }) => id)
    const selectedGroupIds = props.membersSelected.groups.map(({ id }) => id)

    const unselectedUsers =
      users.filter((user: IMembersSelectorUser) => {
        return !selectedUserIds.find((id) => {
          return id === user.id
        })
      }) ?? []

    const unselectedGroups =
      groups.filter((group: IMembersSelectorGroup) => {
        return !selectedGroupIds.find((id) => {
          return id === group.id
        })
      }) ?? []

    return { unselectedGroups, unselectedUsers }
  }

  return (
    <div className="MembersSelector">
      <Field stretch>
        <ResettableInput
          placeholder={
            featureFlags?.account_name_login
              ? t`Enter names, account names, or email addresses`
              : t`Enter names, or email addresses`
          }
          autoComplete="new-password"
          onFocus={() => {
            setStateWrapped({
              showAutocomplete: !!(
                !state.isAddressBookActive && state.query.length
              ),
              isAddressBookActive: false
            })
          }}
          value={state.query}
          onChange={(value) => {
            setStateWrapped({
              query: value,
              showAutocomplete: !!value.length
            })

            changeQuery(value)
          }}
          title={t`Members selector`}
          showResetButton={!!state.query}
          onReset={() => {
            setStateWrapped({
              query: '',
              isAddressBookActive: false,
              showAutocomplete: false
            })
            changeQuery('')
          }}
          inputRef={props.inputRef}
        />
        {props.addressBook && (
          <>
            <span className="or">
              <Trans>or</Trans>
            </span>
            <ButtonLight
              accessibilityTitle="Groups"
              iconOnly
              active={state.isAddressBookActive}
              onClick={() => {
                const { unselectedUsers, unselectedGroups } =
                  getUnselectedUsersGroups(
                    props.addressBook?.users || [],
                    props.addressBook?.groups || []
                  )

                setStateWrapped({
                  isAddressBookActive: !state.isAddressBookActive,
                  showAutocomplete: state.query.length
                    ? true
                    : !state.showAutocomplete,
                  autocompleteUsers: unselectedUsers,
                  autocompleteGroups: unselectedGroups,
                  query: ''
                })
              }}
            >
              <IconPeople />
            </ButtonLight>
          </>
        )}
      </Field>

      {state.showAutocomplete && renderAutocomplete()}
      {renderSelected()}
    </div>
  )
}

export default MembersSelector
